import { v5 as uuidv5, validate as uuidValidate } from 'uuid';
import { logger } from '@/lib/logger';

const RFC4122_PATTERN = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
const UUID_V5_FALLBACK_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';

type NodeRandomUUID = (() => string) | null | undefined;

let cachedNodeRandomUUID: NodeRandomUUID;

function fallbackRandomUUID(): string {
  return RFC4122_PATTERN.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function getNodeRandomUUID(): (() => string) | undefined {
  if (cachedNodeRandomUUID !== undefined) {
    return cachedNodeRandomUUID ?? undefined;
  }

  try {
    const dynamicRequire = (Function(
      'return typeof require === "function" ? require : undefined;'
    )() as ((moduleId: string) => unknown) | undefined);

    if (!dynamicRequire) {
      cachedNodeRandomUUID = null;
      return undefined;
    }

    const nodeCrypto = dynamicRequire('crypto') as { randomUUID?: () => string } | undefined;
    if (nodeCrypto && typeof nodeCrypto.randomUUID === 'function') {
      cachedNodeRandomUUID = nodeCrypto.randomUUID.bind(nodeCrypto);
      return cachedNodeRandomUUID ?? undefined;
    }
  } catch (error) {
    logger.warn('[uuid] Node crypto.randomUUID unavailable, continuing without it', { error });
  }

  cachedNodeRandomUUID = null;
  return undefined;
}

/**
 * Generates a UUID using the best available source of randomness, falling back
 * to a Math.random-based implementation when crypto APIs are unavailable.
 */
export const randomUUID = (): string => {
  try {
    const candidate = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (candidate && typeof candidate.randomUUID === 'function') {
      return candidate.randomUUID();
    }
  } catch (error) {
    logger.warn('[uuid] globalThis.crypto.randomUUID failed, falling back', { error });
  }

  const nodeRandomUUID = getNodeRandomUUID();
  if (nodeRandomUUID) {
    try {
      return nodeRandomUUID();
    } catch (error) {
      logger.warn('[uuid] node randomUUID failed, falling back', { error });
      cachedNodeRandomUUID = null;
    }
  }

  return fallbackRandomUUID();
};

export const uuidV5 = (name: string, namespace: string): string => {
  try {
    if (!uuidValidate(namespace)) {
      return uuidv5(`${name}:${namespace}`, UUID_V5_FALLBACK_NAMESPACE);
    }
    return uuidv5(name, namespace);
  } catch (error) {
    logger.warn('[uuid] uuidV5 failed, falling back to randomUUID', { error });
    return randomUUID();
  }
};
